<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '../../../../components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { default as FluentIcon } from 'shared/components/FluentIcon/Icon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';
import PipelinesAPI from 'dashboard/api/pipelines';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  templates: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'pipeline-created']);
const { t } = useI18n();

const currentStep = ref('welcome'); // 'welcome' | 'templates' | 'creating'
const selectedTemplate = ref(null);
const creating = ref(false);

// Templates com ícones e descrições melhoradas
const templatesWithDetails = computed(() => {
  return props.templates.map(template => ({
    ...template,
    iconColor:
      {
        sales: 'text-blue-500',
        support: 'text-purple-500',
        recruitment: 'text-green-500',
      }[template.id] || 'text-slate-500',
    bgColor:
      {
        sales: 'bg-blue-50 dark:bg-blue-900/20',
        support: 'bg-purple-50 dark:bg-purple-900/20',
        recruitment: 'bg-green-50 dark:bg-green-900/20',
      }[template.id] || 'bg-slate-50',
  }));
});

const handleChooseTemplate = () => {
  currentStep.value = 'templates';
};

const handleStartFromScratch = () => {
  emit('close');
  // Emit evento para abrir modal de criar funil vazio
  setTimeout(() => {
    emitter.emit('open-new-pipeline-modal');
  }, 300);
};

const handleTemplateSelect = template => {
  selectedTemplate.value = template;
};

const handleCreateFromTemplate = async () => {
  if (!selectedTemplate.value) return;

  creating.value = true;
  try {
    // Formata o template para criação
    const payload = {
      name: selectedTemplate.value.name,
      description: selectedTemplate.value.description,
      active: true,
      stages: Object.entries(selectedTemplate.value.stages).reduce(
        (acc, [id, stage]) => ({
          ...acc,
          [id]: {
            ...stage,
            id,
            message_templates: stage.message_templates || [],
            position: stage.position,
          },
        }),
        {}
      ),
      settings: {
        agents: [],
      },
    };

    const response = await PipelinesAPI.create(payload);

    emitter.emit('newToastMessage', {
      message: t('PIPELINE.WELCOME.SUCCESS_MESSAGE'),
      action: { type: 'success' },
    });

    emit('pipeline-created', response.data);
    emit('close');
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.ERRORS.CREATE_PIPELINE'),
      action: { type: 'error' },
    });
  } finally {
    creating.value = false;
  }
};

const handleBack = () => {
  if (currentStep.value === 'templates') {
    currentStep.value = 'welcome';
    selectedTemplate.value = null;
  }
};
</script>

<template>
  <Modal :show="show" size="large" @close="$emit('close')">
    <!-- Welcome Step -->
    <div v-if="currentStep === 'welcome'" class="p-8">
      <div class="text-center mb-8">
        <div
          class="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600"
        >
          <FluentIcon
            icon="briefcase"
            size="40"
            :icons="dashboardIcons"
            class="text-white"
          />
        </div>
        <h2 class="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-3">
          {{ t('PIPELINE.WELCOME.TITLE') }}
        </h2>
        <p class="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
          {{ t('PIPELINE.WELCOME.DESCRIPTION') }}
        </p>
      </div>

      <!-- Options -->
      <div class="grid grid-cols-2 gap-6 max-w-4xl mx-auto">
        <!-- Use Template -->
        <button
          class="group relative p-6 border-2 border-slate-200 dark:border-slate-700 rounded-xl hover:border-blue-500 dark:hover:border-blue-500 transition-all duration-200 text-left bg-white dark:bg-slate-800 hover:shadow-lg"
          @click="handleChooseTemplate"
        >
          <div class="flex flex-col h-full">
            <div
              class="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-lg bg-blue-50 dark:bg-blue-900/20 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 transition-colors"
            >
              <FluentIcon
                icon="copy"
                size="32"
                :icons="dashboardIcons"
                class="text-blue-500 group-hover:scale-110 transition-transform"
              />
            </div>
            <h3
              class="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2"
            >
              {{ t('PIPELINE.WELCOME.USE_TEMPLATE') }}
            </h3>
            <p class="text-sm text-slate-600 dark:text-slate-400 flex-1">
              {{ t('PIPELINE.WELCOME.USE_TEMPLATE_DESC') }}
            </p>
            <div class="mt-4 flex items-center text-blue-500 font-medium">
              <span class="text-sm">{{
                t('PIPELINE.WELCOME.CHOOSE_TEMPLATE')
              }}</span>
              <FluentIcon
                icon="arrow-chevron-right"
                size="16"
                :icons="dashboardIcons"
                class="ml-2 group-hover:translate-x-1 transition-transform"
              />
            </div>
          </div>
        </button>

        <!-- Start from Scratch -->
        <button
          class="group relative p-6 border-2 border-slate-200 dark:border-slate-700 rounded-xl hover:border-purple-500 dark:hover:border-purple-500 transition-all duration-200 text-left bg-white dark:bg-slate-800 hover:shadow-lg"
          @click="handleStartFromScratch"
        >
          <div class="flex flex-col h-full">
            <div
              class="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-lg bg-purple-50 dark:bg-purple-900/20 group-hover:bg-purple-100 dark:group-hover:bg-purple-900/30 transition-colors"
            >
              <FluentIcon
                icon="add-circle"
                size="32"
                :icons="dashboardIcons"
                class="text-purple-500 group-hover:scale-110 transition-transform"
              />
            </div>
            <h3
              class="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2"
            >
              {{ t('PIPELINE.WELCOME.START_FROM_SCRATCH') }}
            </h3>
            <p class="text-sm text-slate-600 dark:text-slate-400 flex-1">
              {{ t('PIPELINE.WELCOME.START_FROM_SCRATCH_DESC') }}
            </p>
            <div class="mt-4 flex items-center text-purple-500 font-medium">
              <span class="text-sm">{{
                t('PIPELINE.WELCOME.CREATE_CUSTOM')
              }}</span>
              <FluentIcon
                icon="arrow-chevron-right"
                size="16"
                :icons="dashboardIcons"
                class="ml-2 group-hover:translate-x-1 transition-transform"
              />
            </div>
          </div>
        </button>
      </div>
    </div>

    <!-- Templates Step -->
    <div v-if="currentStep === 'templates'" class="p-8">
      <div class="flex items-center gap-4 mb-6">
        <button
          class="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          @click="handleBack"
        >
          <FluentIcon
            icon="arrow-chevron-left"
            size="20"
            :icons="dashboardIcons"
            class="text-slate-600 dark:text-slate-400"
          />
        </button>
        <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100">
          {{ t('PIPELINE.WELCOME.SELECT_TEMPLATE') }}
        </h2>
      </div>

      <div class="grid gap-4 mb-6">
        <div
          v-for="template in templatesWithDetails"
          :key="template.id"
          class="group relative p-6 border-2 rounded-xl cursor-pointer transition-all duration-200"
          :class="[
            selectedTemplate?.id === template.id
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700 bg-white dark:bg-slate-800',
          ]"
          @click="handleTemplateSelect(template)"
        >
          <div class="flex items-start gap-4">
            <div
              class="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-lg"
              :class="template.bgColor"
            >
              <FluentIcon
                :icon="template.icon"
                size="24"
                :icons="dashboardIcons"
                :class="template.iconColor"
              />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between mb-2">
                <h3
                  class="text-lg font-semibold text-slate-900 dark:text-slate-100"
                >
                  {{ template.name }}
                </h3>
                <div
                  v-if="selectedTemplate?.id === template.id"
                  class="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500"
                >
                  <FluentIcon
                    icon="checkmark"
                    size="16"
                    :icons="dashboardIcons"
                    class="text-white"
                  />
                </div>
              </div>
              <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
                {{ template.description }}
              </p>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="(stage, stageId) in template.stages"
                  :key="stageId"
                  class="px-3 py-1 text-xs font-medium rounded-full"
                  :style="{
                    backgroundColor: `${stage.color}20`,
                    color: stage.color,
                  }"
                >
                  {{ stage.name }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-3">
        <Button variant="ghost" color="slate" @click="handleBack">
          {{ t('PIPELINE.WELCOME.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="blue"
          :disabled="!selectedTemplate || creating"
          @click="handleCreateFromTemplate"
        >
          <template v-if="creating" #icon>
            <span class="loading-spinner" />
          </template>
          {{
            creating
              ? t('PIPELINE.WELCOME.CREATING')
              : t('PIPELINE.WELCOME.CREATE_PIPELINE')
          }}
        </Button>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
